// Why notifications can't be turned on here, and what to do about it
// (D307, D308, issue #56). "Not available" told people nothing; each reason
// names the fix for that browser. Pure where it can be, so tests pin down
// every case without a real browser.

import { isAppleMobile, isInstalledApp } from './pwa';

export type PushProblem =
  /** iPhone/iPad Safari tab: only the Home Screen app gets them (D139, D185). */
  | 'appleTab'
  /** Plain http: no service worker, so no push. */
  | 'insecure'
  /** Firefox without service workers: a private window. */
  | 'privateWindow'
  /** No service worker for another reason (an app's built-in browser, an old one). */
  | 'noServiceWorker'
  /** Service workers, but no Push API or notifications. */
  | 'unsupported'
  /** Twinden's service worker didn't register or never got ready (#46). */
  | 'swFailed'
  /** Permission denied, for this site or by a private window. */
  | 'blocked'
  /** Brave with "Use Google services for push messaging" off. */
  | 'brave'
  /** The browser's push service refused or didn't answer. */
  | 'pushService'
  /** Anything else `subscribe()` threw; the reason is shown with it. */
  | 'subscribeFailed';

export interface PushEnvironment {
  secure: boolean;
  serviceWorker: boolean;
  pushManager: boolean;
  notification: boolean;
  userAgent: string;
  maxTouchPoints: number;
  installed: boolean;
  /** Brave says so with `navigator.brave`. */
  brave: boolean;
}

/** This browser, as the checks below see it. */
export function pushEnvironment(): PushEnvironment {
  const nav = navigator as Navigator & { brave?: unknown };
  return {
    secure:
      typeof window.isSecureContext === 'boolean'
        ? window.isSecureContext
        : true,
    serviceWorker: 'serviceWorker' in navigator && !!navigator.serviceWorker,
    pushManager: 'PushManager' in window,
    notification: 'Notification' in window,
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    installed: isInstalledApp(),
    brave: Boolean(nav.brave),
  };
}

/**
 * Why push can't work in this browser at all, before anyone asks; null
 * when it might. The order matters: an iPhone tab is told to install even
 * though it lacks the Push API, plain http is the cause before anything
 * else.
 */
export function unsupportedReason(env: PushEnvironment): PushProblem | null {
  if (isAppleMobile(env.userAgent, env.maxTouchPoints) && !env.installed) {
    return 'appleTab';
  }
  if (!env.secure) return 'insecure';
  if (!env.serviceWorker) {
    // Firefox has service workers everywhere but in private windows.
    return /Firefox\//.test(env.userAgent)
      ? 'privateWindow'
      : 'noServiceWorker';
  }
  if (!env.pushManager || !env.notification) return 'unsupported';
  return null;
}

/** Thrown by the hook when the service worker never gets ready. */
export class PushSetupError extends Error {
  constructor(readonly problem: PushProblem) {
    super(problem);
    this.name = 'PushSetupError';
  }
}

/**
 * Why turning notifications on failed. Brave's push service is off by
 * default, and then `subscribe()` fails with an AbortError ("Registration
 * failed - push service error"); other browsers say the same when their
 * push service is down or a private window has none.
 */
export function subscribeProblem(
  err: unknown,
  env: Pick<PushEnvironment, 'brave'>
): PushProblem {
  if (err instanceof PushSetupError) return err.problem;
  const name = (err as { name?: unknown } | null)?.name;
  const message = String((err as { message?: unknown } | null)?.message ?? '');
  if (name === 'NotAllowedError' || /permission denied/i.test(message)) {
    return 'blocked';
  }
  const pushService =
    name === 'AbortError' || /push service|registration failed/i.test(message);
  if (pushService) return env.brave ? 'brave' : 'pushService';
  return 'subscribeFailed';
}

/** The catalog key for a problem's text (`account.push.why.*`). */
export function problemKey(problem: PushProblem) {
  return `account.push.why.${problem}` as const;
}

/** What the browser said, short, for 'subscribeFailed'. */
export function errorReason(err: unknown): string {
  const message = String((err as { message?: unknown } | null)?.message ?? '');
  return (message || String(err)).slice(0, 160);
}

/**
 * The service worker, or a PushSetupError('swFailed') when it didn't
 * register: `serviceWorker.ready` never settles then, which used to leave
 * the button spinning forever.
 */
export async function readyRegistration(
  timeoutMs = 10_000
): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker
    .getRegistration()
    .catch(() => undefined);
  if (existing?.active) return existing;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new PushSetupError('swFailed')),
          timeoutMs
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
